import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Divider,
    CircularProgress,
} from '@material-ui/core';
import { useOnMount, useOnUnmount } from 'utils/react';
import { generateRecoveryCodes } from '../../service';
import ButtonsForm from 'ui/form/ButtonsForm';
import DialogButtons from 'ui/dialog/Buttons';
import ClipboardJS from 'clipboard';

const useStyles = makeStyles(theme => ({
    helperText: {
        padding: `${theme.spacing()}px 0px`,
        position: 'relative',
    },
    helperBack: {
        position: 'absolute',
        backgroundColor: '#FDFBDE',
        left: -24,
        right: -24,
        height: '100%',
    },
    helperContainer: {
        position: 'relative',
    },
    codeListRoot: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(3),
        flexDirection: 'column',
    },
    codeList: {
        width: 500,
        fontSize: '1.5rem',
        fontWeight: 600,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginBottom: theme.spacing(3),
    },
    codeListItem: {
        width: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'monospace',
    },
    codeListItemDot: {
        width: 5,
        height: 5,
        borderRadius: '100%',
        marginRight: theme.spacing(),
        backgroundColor: '#BFBFBF',
    },
    codeListItemUsed: {
        color: '#f00',
    },
    divider: {
        position: 'relative',
        margin: `${theme.spacing(3)}px -${theme.spacing(3)}px`,
    },
    generateButton: {
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: theme.spacing(2),
        display: 'inline-block',
    },
}));

export const RecoveryCodesDialog = ({ onSubmit, setup, open, data }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [recoveryData, setRecoveryData] = useState(data);

    const handleGenerateCodes = async () => {
        setLoading(true);
        const newData = await generateRecoveryCodes({
            confirmCode: recoveryData.confirm_code,
        });
        setRecoveryData(newData);
        setLoading(false);
    };

    const handleSubmit = () => {
        onSubmit(recoveryData);
    };

    return (
        <Dialog open={open} fullWidth>
            <DialogTitle>
                Recovery codes
                <Typography color="textSecondary" variant="body2">
                    Recovery codes are single-use codes that can be used to
                    access your account in the event you cannot receive
                    two-factor authentication codes.
                </Typography>
            </DialogTitle>
            <DialogContent>
                <div className={classes.helperContainer}>
                    <div className={classes.helperBack}></div>
                    <Typography variant="body2" className={classes.helperText}>
                        Copy and save your recovery codes. We recommend saving
                        your recovery codes in the CyberFOX Password Manager
                        app.
                    </Typography>
                </div>
                <CodesList classes={classes} codes={recoveryData.codes} />
                {!setup && (
                    <>
                        <Divider className={classes.divider} />
                        <Typography style={{ fontWeight: 500 }} variant="body2">
                            Generate new recovery codes
                        </Typography>
                        <Typography color="textSecondary" variant="body2">
                            When you generate new recovery codes, your old codes
                            won't work anymore.
                        </Typography>
                        <Typography
                            className={classes.generateButton}
                            color="secondary"
                            variant="body2"
                            onClick={handleGenerateCodes}
                        >
                            Generate new recovery codes
                        </Typography>
                        {loading && (
                            <CircularProgress
                                size={16}
                                style={{ marginLeft: 8 }}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <DialogButtons>
                    <ButtonsForm.NextButton
                        label={setup ? 'NEXT' : 'BACK TO SETTINGS'}
                        onClick={handleSubmit}
                    />
                </DialogButtons>
            </DialogActions>
        </Dialog>
    );
};

const CodesList = ({ classes, codes = [] }) => {
    const copyRef = useRef(null);
    const containerRef = useRef(null);
    const [clipboard, setClipboard] = useState(null);

    useOnMount(() => {
        const clipboard = new ClipboardJS(copyRef.current, {
            target: () => containerRef.current,
        });
        setClipboard(clipboard);
    });

    useOnUnmount(() => {
        if (clipboard) {
            clipboard.destroy();
        }
    });

    return (
        <div className={classes.codeListRoot}>
            <div className={classes.codeList} ref={containerRef}>
                {codes.map(code => (
                    <CodeListItem classes={classes} used={code.used}>
                        {code.code}
                    </CodeListItem>
                ))}
            </div>
            <div style={{ textAlign: 'center' }}>
                <Button ref={copyRef}>Copy</Button>
            </div>
        </div>
    );
};

const CodeListItem = ({ children, classes, used }) => {
    return (
        <div
            className={classNames(classes.codeListItem, {
                [classes.codeListItemUsed]: used,
            })}
        >
            <div className={classes.codeListItemDot}></div>
            {children}
        </div>
    );
};
